import { useCreateUnprocessedOrderConfirmModal } from "features/useCreateUnprocessedOrderConfirmModal";
import { useMemo, useEffect } from "react";
import { useHistory } from "react-router";

import { PAYMENT_STATUS } from "./constants/PaymentStatus";
import { useEventImages } from "./hooks/useEventImages";
import { useOrderAddress } from "./hooks/useOrderAddress";
import { useOrderData } from "./hooks/useOrderData";
import { useOrderFulfillment } from "./hooks/useOrderFulfillment";
import { useOrderList } from "./hooks/useOrderList";
import { useOrderTransaction } from "./hooks/useOrderTransaction";
import { useReceiptState } from "./hooks/useReceiptState";
import { useTicketBonusMap } from "./hooks/useTicketBonusMap";

import type { PATH_PARAMETERS } from "constants/pathParameters";
import type { GetParameters } from "types/GetParameters";

type Parameters = GetParameters<
  [(typeof PATH_PARAMETERS)["/account/settlement/history/:orderId"]]
>;

type Input = {
  parameters: Parameters;
};

export const useThis = (input: Input) => {
  const {
    parameters: { orderId },
  } = input;
  const history = useHistory();
  const { transaction, isInvalidTransaction } = useOrderTransaction({
    orderId,
  });

  const { addressData, isLoading: isLoadingAddress } = useOrderAddress({
    transaction,
  });
  const { fulfillmentData, isLoading: isLoadingFulfillment } =
    useOrderFulfillment({
      transaction,
    });
  const {
    eventImageMap,
    eventPartsMap,
    isLoading: isImageLoading,
  } = useEventImages({
    transaction,
  });
  const { bonusMap, isLoadingBonus } = useTicketBonusMap({ transaction });

  const { orderData } = useOrderData({ transaction });
  const { orderList } = useOrderList({
    transaction,
    eventImageMap,
    eventPartsMap,
    bonusMap,
    isImageLoading,
  });

  const isPurchased =
    !isNaN(Number(orderData?.orderId)) &&
    orderData?.paymentStatus === PAYMENT_STATUS.PAYSUCCESS;

  const {
    isDisplayReceipt,
    receiptData,
    handleShowReceipt,
    handleCloseReceipt,
  } = useReceiptState({
    orderData,
    orderList,
  });

  useEffect(() => {
    if (isInvalidTransaction) history.replace("/account/settlement/history");
  }, [isInvalidTransaction, history]);

  const deliveryData = useMemo(() => {
    if (!addressData) return null;

    return {
      recipientName: addressData.recipientName,
      postalCode: addressData.zipCode,
      mainAddress: `${addressData.prefecture}${addressData.city}${addressData.streetNum}`,
      subAddress: addressData.buildingInfo,
    };
  }, [addressData]);

  const handleCopyOrderId = async (orderId: number) => {
    await navigator.clipboard.writeText(String(orderId));
  };

  const { createConfirmCvsPaymentData, renderConfirmCvsPayment } =
    useCreateUnprocessedOrderConfirmModal();

  const handleContinuePayment = () => {
    if (transaction) {
      createConfirmCvsPaymentData(transaction);
    }
  };

  return {
    deliveryData,
    fulfillmentData,
    handleCloseReceipt,
    handleContinuePayment,
    handleCopyOrderId,
    handleShowReceipt,
    isDisplayReceipt,
    isLoadingAddress,
    isLoadingBonus,
    isLoadingFulfillment,
    isPurchased,
    orderData,
    orderList,
    receiptData,
    renderConfirmCvsPayment,
    transaction,
  };
};
